import { css } from '@emotion/react'
import { useCallback } from 'react'
import {
    openAuthenticationDrawer,
    openSignupDrawer,
} from '../../../app-state/modals/state.drawer'
import { SecureStateType } from '../../../constants/enums'
import { logout } from '../../../mfcLogic/auth'
import { UserAccess } from '../../../mfcLogic/userProperties/level'
import { AgeVerificationProvider } from '../../../state/state.secure'
import Button from '../../Buttons/Button'
import { useSecureState } from '../../Hooks/useSecureState'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import { maxWindowWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import { PAPER_HEIGHTS } from '../../theme'
import Link from '../Link'

const smallerDeviceFontSize = css({
    fontSize: '.9rem',
    [maxWindowWidth(390)]: {
        fontSize: '.85rem',
    },
})

const rootStyles = css({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    margin: 'auto',
    zIndex: PAPER_HEIGHTS.quickReply,
    background: palette.background,
})

const sectionStyles = css({
    padding: 16,
    maxWidth: 508,
})

const headerTextStyles = css({
    fontSize: '1.2rem',
    fontWeight: 700,
    paddingBottom: 8,
})

const subHeaderText = css({
    fontSize: '1rem',
    fontWeight: 700,
    paddingBottom: 8,
})

const guestUserButtonStyles = css({
    marginBottom: getThemeSpacing(1.5),
    padding: getThemeSpacing(2),
    textTransform: 'none' as const,
    fontWeight: 600,
})

const verificationProviderStyles = css({
    padding: getThemeSpacing(1.5),
    textTransform: 'none' as const,
    fontWeight: 600,
    [maxWindowWidth(380)]: {
        fontSize: 12,
    },
})

const borderStyles = css({
    border: `1px solid ${palette.buttonBorder}`,
    borderRadius: 8,
    padding: 12,
})

const promotionBackground = css({
    background: palette.informationBackground,
    borderRadius: shape.borderRadiusMediumLarge,
    display: 'flex',
    justifyContent: 'center',
})

const promotionStyles = css({
    color: palette.informationText,
    fontSize: '.9rem',
    textAlign: 'left',
    padding: 0,
    margin: 0,
})

const greyLinkStyles = css({
    color: `${palette.greyMFC} !important`,
})

const verificationProviderText = (providers: Array<string>) => {
    if (providers.length === 2) {
        return ' We currently have two methods of age verification available:'
    }
    return ''
}

const promotionWrapperStyles = css({
    marginBottom: getThemeSpacing(1.5),
    marginTop: getThemeSpacing(1),
    textTransform: 'none' as const,
    fontWeight: 600,
})

const PromotionBanner = (props: { perks: Array<string> }) => {
    const { perks } = props
    return (
        <div css={promotionBackground} data-testid="VerificationPromotion">
            <ul css={promotionStyles}>
                {perks.map((perk, idx) => {
                    return <li key={idx}>{perk}</li>
                })}
            </ul>
        </div>
    )
}

const VerificationProvider = (props: {
    key: string
    providerName: string
    provider: AgeVerificationProvider
}) => {
    const { key, providerName, provider } = props
    const perks = provider?.perks
    // there will be multiple ondato providers setup (per diff regions)
    const isOndato = providerName.includes('ondato')
    const redirectToVerificationProvider = useCallback(
        (providerName: string) => {
            console.warn(
                `Redirecting user to Bouncer via ${providerName} for age verification`,
            )
            const returnUrl = encodeURIComponent(
                document ? document?.URL : 'https://app.myfreecams.com',
            )
            const bouncerBaseUrl =
                provider.url ||
                `https://bouncer.myfreecams.com/?provider=${providerName}&return_url=${returnUrl}`
            window.location.href = bouncerBaseUrl
            console.warn('Redirect started', bouncerBaseUrl)
        },
        [provider],
    )
    return (
        <div
            key={key}
            data-testid={`VerificationProvider__${providerName}`}
            css={[
                borderStyles,
                {
                    paddingBottom: 0,
                    marginBottom: getThemeSpacing(2),
                },
            ]}
        >
            {isOndato ? (
                <img
                    className="age-gate-image"
                    src={`/static/logos/ONDATO-logo.svg`}
                    css={{
                        width: 140,
                        [maxWindowWidth(390)]: {
                            width: 110,
                        },
                    }}
                />
            ) : (
                <>
                    <img
                        className="age-gate-image"
                        src={`/static/logos/yoti-logo.png`}
                        css={{
                            width: 120,
                            [maxWindowWidth(390)]: {
                                width: 90,
                            },
                        }}
                    />
                    <br />
                </>
            )}
            <Button
                data-testid={`AgeGate__VerifyButton${providerName}`}
                css={verificationProviderStyles}
                onClick={() => redirectToVerificationProvider(providerName)}
                size="large"
            >
                {isOndato
                    ? `Take a picture of your I.D.`
                    : `Estimate your age by WebCam`}
            </Button>
            {perks && (
                <div
                    data-testid={`Verification__${providerName}Promotion`}
                    css={promotionWrapperStyles}
                >
                    <PromotionBanner perks={perks} />
                </div>
            )}
        </div>
    )
}

const AgeGateNotice = () => {
    const { ageGateActive, ageVerificationProviders } = useSecureState(
        SecureStateType.AGE_VERIFICATION,
    )

    const level = useUserSelector('me', 'level')
    const name = useUserSelector('me', 'name')
    const shouldRedirectToBouncer = ageGateActive && level > UserAccess.GUEST
    if (shouldRedirectToBouncer) {
        console.log('Age Gate Detected')
    }
    const ageVerificationProvidersByName = Object.keys(ageVerificationProviders)

    const needsReVerification =
        ageVerificationProvidersByName.includes('ondato_flow2')

    const EnabledProviders = Array.from(ageVerificationProvidersByName)
        .filter(
            (provider: string) => ageVerificationProviders[provider].enabled,
        )
        .map((provider, idx) => {
            const providerDetails = ageVerificationProviders[provider]
            return (
                <VerificationProvider
                    key={idx.toString()}
                    providerName={provider}
                    provider={providerDetails}
                />
            )
        })

    return (
        <div
            data-testid="App__AgeGateNotice"
            className="no-scrollbars"
            css={rootStyles}
        >
            <section css={sectionStyles}>
                <div css={headerTextStyles}>Age Verification</div>
                <div css={subHeaderText}>
                    You must be 18+ to access MyFreeCams
                </div>
                {(level === UserAccess.GUEST || !level) && (
                    <>
                        <div
                            css={[
                                smallerDeviceFontSize,
                                {
                                    paddingBottom: 16,
                                },
                            ]}
                        >
                            Due to the laws in your region, you need to verify
                            your age. This will only take a minute and you can
                            use your webcam or phone.
                        </div>
                    </>
                )}
                {(level === UserAccess.GUEST || !level) && (
                    <>
                        <Button
                            data-testid="AgeGate__SignupButton"
                            css={guestUserButtonStyles}
                            onClick={openSignupDrawer}
                            size="large"
                        >
                            Create Free Account
                        </Button>
                        <Button
                            data-testid="AgeGate__LoginButton"
                            css={guestUserButtonStyles}
                            onClick={() => openAuthenticationDrawer()}
                            size="large"
                        >
                            Log In - Already a Member
                        </Button>
                    </>
                )}
                {level > UserAccess.GUEST && (
                    <div css={[smallerDeviceFontSize, { marginBottom: 16 }]}>
                        Due to the laws in your region, you need to verify your
                        age.{' '}
                        {!needsReVerification &&
                            'You only need to verify your age once.'}
                        {verificationProviderText(
                            ageVerificationProvidersByName,
                        )}
                    </div>
                )}
                {level > UserAccess.GUEST && (
                    <div>
                        {EnabledProviders}
                        <div>
                            <em style={{ fontSize: '.8rem' }}>
                                You are logged in as <strong>{name}</strong>
                            </em>
                            <br />
                            <Link
                                onClick={logout}
                                title="logout"
                                inline
                                actionLink
                                mfc
                                css={smallerDeviceFontSize}
                            >
                                Log Out
                            </Link>{' '}
                            •{' '}
                            <Link
                                href="https://wiki.myfreecams.com/wiki/Contact_Support"
                                title="Contact Us"
                                inline
                                mfc
                                css={smallerDeviceFontSize}
                            >
                                Support
                            </Link>
                        </div>
                    </div>
                )}
            </section>
            <footer style={{ fontSize: '.6rem' }}>
                <Link
                    href="https://wiki.myfreecams.com/wiki/18_U.S.C._2257_Record_Keeping_Requirements_Compliance_Statement"
                    title="MFC Record Keeping Requirements"
                    inline
                    underline
                    css={greyLinkStyles}
                >
                    18 U.S.C. 2257 Record Keeping Requirements Compliance
                    Statement
                </Link>
                <br />
                <br />
                <Link
                    href="https://wiki.myfreecams.com/wiki/"
                    title="Privacy Policy"
                    inline
                    underline
                    css={greyLinkStyles}
                >
                    Wiki
                </Link>{' '}
                •{' '}
                <Link
                    href="https://wiki.myfreecams.com/wiki/Terms_and_Conditions"
                    title="Terms & Conditions"
                    inline
                    underline
                    css={greyLinkStyles}
                >
                    Terms
                </Link>{' '}
                •{' '}
                <Link
                    href="https://wiki.myfreecams.com/wiki/Contact_Support"
                    title="Contact Us"
                    inline
                    underline
                    css={greyLinkStyles}
                >
                    Contact Us
                </Link>{' '}
                •{' '}
                <Link
                    href="https://wiki.myfreecams.com/wiki/Becoming_a_Webcam_Model_at_MyFreeCams"
                    title="Become a Model"
                    inline
                    underline
                    css={greyLinkStyles}
                >
                    Information on becoming a model
                </Link>
            </footer>
        </div>
    )
}

export default AgeGateNotice
